import { useQuery } from '@apollo/client';
import React, { FC } from 'react';
import * as S from './CategoryDesktopFilter.styles';
import { useCurrentCategory } from '@/hooks/useCurrentCategory/useCurrentCategory';
import { useServerDataContext } from '@/contexts';
import { AngleBackIcon } from '@/components/UI/Icons/Icons';
import { useUrlAndHashFromPath } from '@/hooks/useUrlAndHashFromPath';
import { GetRootCategoriesResponse } from '@/api/types/categories.types';
import { GET_ROOT_CATEGORIES } from '@/api/queries/categories.queries';
import { useParseQuery } from '@/hooks/useParseQuery/useParseQuery';
import { PERSISTED_QUERY_PARAMS } from '@/components/Filters/DesktopFilters/CategoryDesktopFilter.constants';
import { SearchResultsPageTestIds } from '@/__e2e__/testIds/search-results-page';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';

interface Props {
  isLoading?: boolean;
}

export const CategoryDesktopFilter: FC<Props> = ({ isLoading }) => {
  const {
    currentCategory: serverCurrentCategory,
    rootCategories: serverRootCategories,
  } = useServerDataContext();
  const {
    currentCategory = serverCurrentCategory,
    isAllCategories,
    isSubcategories,
  } = useCurrentCategory();
  const { queryString } = useUrlAndHashFromPath();

  const {
    keywords,
    categoryId,
    price,
    regionId,
    propertyOptions,
    userLegacyId,
  } = useParseQuery();

  const currentCategoryId = categoryId || currentCategory?.id;

  const { data } = useQuery<GetRootCategoriesResponse>(GET_ROOT_CATEGORIES, {
    variables: {
      keywords,
      filters: {
        categoryId: isAllCategories ? null : currentCategoryId,
        price,
        regionId,
        propertyOptions,
        userLegacyId,
      },
    },
  });

  const categories = data?.categories ?? serverRootCategories?.categories ?? [];

  function getCategoryPathWithQuery(categoryPath: string) {
    const searchParams = new URLSearchParams(queryString);
    searchParams.forEach((_value, key) => {
      if (!PERSISTED_QUERY_PARAMS.includes(key)) {
        searchParams.delete(key);
      }
    });
    const search = searchParams.toString();
    return search ? `/${categoryPath}?${search}` : `/${categoryPath}`;
  }

  const parentCategories = [
    currentCategory?.parent?.parent,
    currentCategory?.parent,
  ];

  return (
    <S.Container>
      {parentCategories.map(
        parentCategory =>
          parentCategory &&
          (isLoading ? (
            <S.ParentCategoryLoader key={parentCategory.id}>
              <SkeletonLoader width={100} height={18} borderRadius={7} />
            </S.ParentCategoryLoader>
          ) : (
            <S.ParentCategory
              key={parentCategory.id}
              dataTestId={
                SearchResultsPageTestIds.desktopFilters.parentCategory
              }
              href={getCategoryPathWithQuery(parentCategory.path)}
            >
              <AngleBackIcon height={12} width={6} />
              {parentCategory.name}
            </S.ParentCategory>
          ))
      )}
      {isLoading ? (
        <S.CurrentCategoryLoader>
          <SkeletonLoader width={150} height={18} borderRadius={7} />
        </S.CurrentCategoryLoader>
      ) : (
        <S.CurrentCategory
          data-testid={SearchResultsPageTestIds.desktopFilters.currentCategory}
        >
          {currentCategory?.name}
        </S.CurrentCategory>
      )}
      {isLoading && !isSubcategories
        ? Array.from({ length: 14 }, (_, index) => (
            <S.CategoryLoader key={index}>
              <SkeletonLoader width={150} height={18} borderRadius={7} />
            </S.CategoryLoader>
          ))
        : categories.map(category => (
            <S.Category
              key={category.id}
              href={getCategoryPathWithQuery(category.path)}
              dataTestId={SearchResultsPageTestIds.desktopFilters.category}
            >
              {category.name}
            </S.Category>
          ))}
    </S.Container>
  );
};
